import cx from 'classnames'
import Img from 'gatsby-image'
// @ts-ignore
import { getFluidGatsbyImage } from 'gatsby-source-sanity'
import React, { useState, useEffect, useMemo, useRef } from 'react'

import imageUrlBuilder from '@sanity/image-url'
import sanityClient from '@sanity/client'
import { SanityImageSource } from '@sanity/image-url/lib/types/types'

const sanityConfig = {
  projectId: process.env.GATSBY_SANITY_PROJECT_ID,
  dataset: process.env.GATSBY_SANITY_DATASET,
}

const client = sanityClient({
  projectId: process.env.GATSBY_SANITY_PROJECT_ID,
  dataset: process.env.GATSBY_SANITY_DATASET,
  useCdn: false, // `false` if you want to ensure fresh data
})
const builder = imageUrlBuilder(client)

export const loadLazyImage = function () {
  if ('IntersectionObserver' in window) {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.intersectionRatio > 0) {
          if (entry.target.hasAttribute('data-src')) {
            entry.target.setAttribute(
              'src',
              entry.target.getAttribute('data-src') as string
            )
            observer.unobserve(entry.target)
          }
        }
      })
    })
    document.querySelectorAll('.lazy-image').forEach(imageElement => {
      if (
        imageElement.getAttribute('is-observed') != 'true' &&
        imageElement.getAttribute('data-src') != null
      ) {
        imageElement.setAttribute('is-observed', 'true')
        observer.observe(imageElement)
      }
    })
  } else {
    const imgList = document.querySelectorAll('.lazy-image')
    Array.prototype.forEach.call(imgList, function (image) {
      image.setAttribute('src', image.getAttribute('data-src'))
    })
  }
}

export const Image = ({
  imageId,
  mobileImageId,
  className,
  size,
  width,
  alt,
  onClick,
  raw,
  lazy,
  fromArticle,
  cls,
  rawStyleOverride,
  fetchpriority,
  priority,
  onLoad,
}: {
  imageId?: string
  mobileImageId?: string
  width?: number
  alt?: string
  size?: number
  onClick?: any
  raw?: object
  className?: string
  src?: string
  lazy?: boolean
  fromArticle?: boolean
  cls?: string
  rawStyleOverride?: any
  fetchpriority?: 'high' | 'low' | 'auto'
  priority?: boolean
  onLoad?: () => void
}) => {
  const [loaded, setLoaded] = useState(false)
  const [imageWidth, setImageWidth] = useState(768)
  const imageRef = useRef<HTMLImageElement | null>(null)
  const hasReportedLoad = useRef(false)
  const imageFetchPriority = priority ? 'high' : fetchpriority
  const shouldLazyLoad = lazy && imageFetchPriority !== 'high'

  useEffect(() => {
    loadLazyImage()

    if (!window.matchMedia) return
    const width = window.matchMedia('(pointer:coarse)').matches ? 768 : 1920
    setImageWidth(width)
  }, [])

  const { dataSource, sourceUrl } = useMemo(() => {
    const imageSrc: SanityImageSource = (raw
      ? raw
      : imageId) as SanityImageSource

    if (!imageSrc) {
      return {
        dataSource: '',
        sourceUrl: '',
      }
    }

    const requestedWidth =
      'number' === typeof size && size > 0 ? size : imageWidth
    const dataSrc = builder.image(imageSrc)?.width(requestedWidth)

    return {
      dataSource: dataSrc ? dataSrc.url() ?? '' : '',
      sourceUrl: dataSrc ? dataSrc.format('webp').url() ?? '' : '',
    }
  }, [imageId, imageWidth, raw, size])

  const handleImageLoad = () => {
    if (hasReportedLoad.current) return

    hasReportedLoad.current = true
    setLoaded(true)
    onLoad?.()
  }

  useEffect(() => {
    hasReportedLoad.current = false
    setLoaded(false)
  }, [dataSource])

  useEffect(() => {
    const image = imageRef.current

    if (!shouldLazyLoad && dataSource && image?.complete) {
      handleImageLoad()
    }
  }, [dataSource, shouldLazyLoad])

  const fluidProps = useMemo(() => {
    const maxImageWidth =
      'number' === typeof width ? Math.min(imageWidth, width) : imageWidth

    if (!imageId || /gif/.test(imageId)) return undefined

    const desktopFluidProps = getFluidGatsbyImage(
      imageId,
      { maxWidth: maxImageWidth },
      sanityConfig
    )
    const mobileFluidProps =
      mobileImageId && !/gif/.test(mobileImageId)
        ? getFluidGatsbyImage(
            mobileImageId,
            { maxWidth: Math.min(maxImageWidth, 768) },
            sanityConfig
          )
        : undefined

    return mobileFluidProps
      ? [{ ...mobileFluidProps, media: '(max-width: 768px)' }, desktopFluidProps]
      : desktopFluidProps
  }, [imageId, imageWidth, mobileImageId, width])

  return (
    <div onClick={onClick} className={cx('image__block', className)}>
      {fluidProps && (!raw || fromArticle) ? (
        <Img
          loading={!lazy ? 'lazy' : 'auto'}
          className={cx('hidden__image', cls, {
            'lazy-image': !lazy,
          })}
          fluid={fluidProps}
          alt={alt}
          onLoad={handleImageLoad}
        />
      ) : (
        <picture>
          <source srcSet={sourceUrl} type="image/webp"></source>
          <img
            ref={imageRef}
            alt={alt}
            style={rawStyleOverride || {}}
            src={
              shouldLazyLoad
                ? 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII='
                : dataSource
            }
            data-src={shouldLazyLoad ? dataSource : undefined}
            loading={priority ? 'eager' : shouldLazyLoad ? 'lazy' : 'eager'}
            {...(imageFetchPriority && { fetchpriority: imageFetchPriority })}
            className={cx('x y object-fit block', {
              'lazy-image': shouldLazyLoad,
              'is-loaded': loaded,
            })}
            onLoad={handleImageLoad}
          />
        </picture>
      )}
    </div>
  )
}

export type SafeImageProps = {
  imageId: string | undefined
  alt: string
  raw?: object | undefined
}

export const SafeImage = (props: SafeImageProps) => {
  const { imageId, alt = '', raw } = props

  if (!raw) {
    return <></>
  }

  return <Image imageId={imageId} alt={alt} raw={raw} lazy />
}
